import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import { Tabs, Form } from 'antd';
import DspxModal from '../../../common/DspxModal/DspxModal';
import ChessForm from '../../forms/ChessForm/ChessForm';
import ChessCardsTabs from '../../ChessCardsTabs/ChessCardsTabs';
import FlatCountError from './components/FlatCountError';
import EmptyDescription from './components/EmptyDescription';
import { chessFieldsNames } from '../../forms/ChessForm/constants/chessFieldsNames';
import { getFlatCountState, getPorchs, getPreviewData } from './utils';
import { texts } from '../../../../constants/texts';
import { getQueryKeys } from '../../../../queries/queryKeys';
import addFlatsGrid from '../../../../requests/addFlatsGrid';
import styles from './ChessModal.module.scss';

const { TabPane } = Tabs;
const { numberOfEntrances, numberOfFloors, numberOfFlats } = chessFieldsNames;

const AddHouseModalNode = ({
  form,
  onCancel,
  onSuccess,
  address,
  allValues = { error: false },
  ...props
}) => {
  const {
    resetFields,
    validateFieldsAndScroll,
    getFieldsValue,
    validateFields,
  } = form;

  const queryClient = useQueryClient();

  const currentHouse = useSelector(
    (state) => state.housingStockStore.currentHouse
  );

  const flatsGridKeys = getQueryKeys.flatsGrid(currentHouse?.houseId);

  const [previewData, setPreviewData] = useState({});

  const [notEnoughDataForPreview, setNotEnoughDataForPreview] = useState(true);

  const values = getFieldsValue();

  const flatCountState = useMemo(
    () => getFlatCountState(values, currentHouse),
    [values, currentHouse]
  );

  const tabs = {
    form: {
      key: 'form',
      tab: 'Настройки',
      content: (
        <>
          {flatCountState?.error ? (
            <FlatCountError
              flatCount={currentHouse?.flatCount}
              receivedFlatCount={flatCountState.flatCount}
            />
          ) : null}
          <ChessForm form={form} address={address} />
        </>
      ),
    },
    preview: {
      key: 'preview',
      tab: 'Предпросмотр',
      content: notEnoughDataForPreview ? (
        <EmptyDescription />
      ) : (
        <ChessCardsTabs entrances={previewData} />
      ),
    },
  };

  const [tab, setTab] = useState(tabs.form.key);

  const validateFieldsOnTabChange = (err) => {
    if (err) {
      setNotEnoughDataForPreview(true);
    } else {
      const values = getFieldsValue();
      const { porchs } = getPreviewData(values);

      setPreviewData(porchs);
      setNotEnoughDataForPreview(false);
    }
  };

  const addFlatsGridMutation = useMutation(
    ({ values }) => addFlatsGrid(values),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(flatsGridKeys);
        onSuccess();
      },
    }
  );

  const handleTabChange = (key) => {
    if (key === tabs.preview.key) {
      validateFields(
        [numberOfEntrances, numberOfFloors, numberOfFlats],
        validateFieldsOnTabChange
      );
    }

    setTab(key);
  };

  const handleCancel = () => {
    setTab(tabs.form.key);
    resetFields();
    onCancel();
  };

  const handleSubmit = () => {
    validateFieldsAndScroll(async (err, fieldValues) => {
      if (!err && !flatCountState.error) {
        const porchs = getPorchs(fieldValues);

        const values = {
          houseId: currentHouse?.houseId,
          porchCount: Number(fieldValues.numberOfEntrances),
          porchs,
        };

        await addFlatsGridMutation.mutateAsync({ values });

        handleCancel();
      } else {
        setTab(tabs.form.key);
      }
    });
  };

  return (
    <DspxModal
      title={texts.addNewHouse}
      okText={texts.formFlatsGrid}
      cancelText={texts.cancel}
      width={651}
      onOk={handleSubmit}
      onCancel={handleCancel}
      cancelButtonProps={{
        disabled: addFlatsGridMutation.isLoading,
      }}
      confirmLoading={addFlatsGridMutation.isLoading}
      destroyOnClose
      {...props}
    >
      <Tabs
        className={styles.tabs}
        tabBarGutter={0}
        activeKey={tab}
        onChange={handleTabChange}
      >
        {Object.keys(tabs).map((itemKey) => (
          <TabPane tab={tabs[itemKey].tab} key={tabs[itemKey].key}>
            {tabs[itemKey].content}
          </TabPane>
        ))}
      </Tabs>
    </DspxModal>
  );
};

const AddHouseModal = Form.create({ name: 'add_house' })(AddHouseModalNode);

export default AddHouseModal;
